import React, { Fragment } from 'react';
import './style.css';
import dayjs from 'dayjs';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { EventFractionalMainReport } from '@/model/EventFractionalMainReport';
import { Col, Row } from 'reactstrap';
import SuperCollapse from '@/components/sharedComponents/SuperCollapse';
import { CustomTable } from '@/components/Table';
import { ReactComponent as Detail } from '@/assets/images/svg/detail.svg';
import { ReactComponent as Ticket } from '@/assets/images/svg/ticket.svg';
import { EventFractionalOrderReport } from '@/model/EventFractionalOrderReport';
import { Dialog } from '@/components';
import { ActionProps } from '@/components/Dialog';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { ShouldShowModal } from '../types';
import { DetailFractionalSaleContent } from '../components/DetailFractionalSaleContent';
import { CreateFractionalPaymentContent } from '../components/CreateFractionalPaymentContent';

interface StateProps {
  title: string | React.ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  report?: EventFractionalMainReport;
  selectedOrder?: EventFractionalOrderReport;
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  onToggle: () => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
    order,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    order: EventFractionalOrderReport;
  }) => void;
  onChangeFormInput: OnChangeFormInput;
  onCreatePayment: () => void;
}

type Props = StateProps & DispatchProps;
export const EventFractionalReportUI: React.FC<Props> = ({
  title,
  visible,
  onToggle,
  shouldShowModal,
  report,
  onShouldShowModal,
  selectedOrder,
  formData,
  onChangeFormInput,
  formErrors,
  onCreatePayment,
}) => {
  const normalizeCurrencyValue = (value: number): number => (Math.abs(value) < 0.005 ? 0 : value);

  const renderActionDialogToCancel: ActionProps = {
    title: 'Fechar',
    onClick: (): void => onToggle(),
    theme: 'noneBorder',
  };
  return (
    <div className="efr-wrapper">
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        footerBorder={'top'}
        position={'center'}
        isContentWithCard={true}
        actions={[
          {
            [ShouldShowModal.DETAIL]: renderActionDialogToCancel,
            [ShouldShowModal.SALE]: renderActionDialogToCancel,
          }[shouldShowModal],
          {
            [ShouldShowModal.DETAIL]: {},
            [ShouldShowModal.SALE]: {
              title: 'Criar',
              onClick: onCreatePayment,
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.DETAIL]: <DetailFractionalSaleContent order={selectedOrder} />,
            [ShouldShowModal.SALE]: (
              <CreateFractionalPaymentContent
                formData={formData}
                onChangeFormInput={onChangeFormInput}
                formErrors={formErrors}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      {report && (
        <Fragment>
          {report.summary && (
            <Fragment>
              <h5>Totalizadores</h5>
              <div
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '10px',
                  padding: '20px 10px 10px 10px',
                  display: 'inline-block',
                  width: '100%',
                  maxWidth: '600px',
                  marginBottom: '20px',
                  background: '#fff',
                  boxShadow: '4px 4px 10px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Row
                  className="mb-4"
                  style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col md={6}>
                    <div className="text-darkgray-regular">Cartão de crédito:</div>
                  </Col>
                  <Col xs={6} style={{ textAlign: 'right' }}>
                    <div className="text-success">{toCurrency(report.summary.credit)}</div>
                  </Col>
                </Row>
                <Row
                  className="mb-4"
                  style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col xs={6}>
                    <div className="text-darkgray-regular">Cartão de débito:</div>
                  </Col>
                  <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                    <div className="text-success">{toCurrency(report.summary.debit)}</div>
                  </Col>
                </Row>
                <Row
                  className="mb-4"
                  style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col xs={6}>
                    <div className="text-darkgray-regular">PIX:</div>
                  </Col>
                  <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                    <div className="text-success">{toCurrency(report.summary.pix)}</div>
                  </Col>
                </Row>
                <Row
                  className="mb-4"
                  style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col xs={6}>
                    <div className="text-darkgray-regular">Dinheiro:</div>
                  </Col>
                  <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                    <div className="text-success">{toCurrency(report.summary.money)}</div>
                  </Col>
                </Row>
                <Row
                  className="mb-4"
                  style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col xs={6}>
                    <div className="text-darkgray-regular">Cheque:</div>
                  </Col>
                  <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                    <div className="text-success">{toCurrency(report.summary.check)}</div>
                  </Col>
                </Row>
                <Row
                  className="mb-4"
                  // style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
                >
                  <Col xs={6}>
                    <div
                      className="text-darkgray-regular"
                      style={{ fontWeight: 'bold', letterSpacing: '1px' }}
                    >
                      Total:
                    </div>
                  </Col>
                  <Col
                    xs={6}
                    style={{
                      textAlign: 'right',
                      borderBottom: '1px',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                    }}
                  >
                    <div className={report.summary.totalValue > 0 ? 'text-success' : 'text-danger'}>
                      {toCurrency(report.summary.totalValue)}
                    </div>
                  </Col>
                </Row>
              </div>
            </Fragment>
          )}
          {report.sectors && report.sectors.length > 0 && (
            <Fragment>
              <h5>Setores</h5>
              {report.sectors.map((data, index) => (
                <Fragment key={`Sector-${index}-${data.id}`}>
                  <div className="collapseTableText">
                    <div className="d-flex mb-3">{data.name}</div>
                  </div>
                  <SuperCollapse
                    width={'100%'}
                    title={
                      <CustomTable
                        numberRowsPerPage={0}
                        progressPending={false}
                        theme="secondaryWithoutBorder"
                        columns={[
                          {
                            name: 'Total',
                            selector: row => row.total,
                            width: '15%',
                          },
                          {
                            name: 'Vendido',
                            selector: row => row.sold,
                            width: '15%',
                          },
                          {
                            name: 'Disponível',
                            selector: row => row.available,
                            width: '15%',
                          },
                          {
                            name: 'Valor Pago',
                            selector: row => row.soldValue,
                            width: '25%',
                          },
                          {
                            name: 'Valor à Pagar',
                            selector: row => row.remainingValue,
                            width: '15%',
                          },
                        ]}
                        data={[
                          {
                            total: data.total,
                            sold: data.sold,
                            available: data.available,
                            soldValue: toCurrency(data.soldValue),
                            remainingValue: toCurrency(normalizeCurrencyValue(data.remainingValue)),
                          },
                        ]}
                      />
                    }
                    content={
                      <Fragment>
                        {data.orders && data.orders.length > 0 && (
                          <Fragment>
                            <h6>Camarotes</h6>
                            <CustomTable
                              numberRowsPerPage={0}
                              progressPending={false}
                              theme="secondaryWithoutBorder"
                              columns={[
                                {
                                  name: 'Camarote',
                                  selector: row => row.ticket,
                                  width: '25%',
                                },
                                {
                                  name: 'Valor',
                                  selector: row => row.totalValue,
                                  width: '15%',
                                },
                                {
                                  name: 'Data',
                                  selector: row => row.orderDate,
                                  width: '15%',
                                },
                                {
                                  name: 'Valor Pago',
                                  selector: row => row.paidAmount,
                                  width: '15%',
                                },
                                {
                                  name: 'Valor à Pagar',
                                  selector: row => row.remainingValue,
                                  width: '15%',
                                },
                                {
                                  name: 'Ações',
                                  selector: row => row.actions,
                                  width: '15%',
                                },
                              ]}
                              data={
                                data.orders && data.orders.length > 0
                                  ? data.orders.map(order => ({
                                      ticket: (
                                        <div>
                                          <strong>
                                            {order.label
                                              ? `${order.ticket} - ${order.label}`
                                              : order.ticket}
                                          </strong>
                                        </div>
                                      ),
                                      totalValue: toCurrency(order.totalValue) || '---',
                                      orderDate:
                                        dayjs(order.orderDate)
                                          .subtract(3, 'hour')
                                          .format('DD/MM/YYYY [às] HH:mm') || '---',
                                      paidAmount: toCurrency(order.paidAmount) || '---',
                                      remainingValue:
                                        normalizeCurrencyValue(order.remainingValue) > 0 ? (
                                          <span className="text-danger">
                                            {toCurrency(
                                              normalizeCurrencyValue(order.remainingValue),
                                            )}
                                          </span>
                                        ) : (
                                          <span className="text-success">
                                            {toCurrency(
                                              normalizeCurrencyValue(order.remainingValue),
                                            )}
                                          </span>
                                        ),
                                      actions: (
                                        <Fragment>
                                          <Detail
                                            className={'mr-4 svg-icon action-icon'}
                                            onClick={() =>
                                              onShouldShowModal({
                                                value: ShouldShowModal.DETAIL,
                                                newTitleModal: 'Detalhes do pedido',
                                                order,
                                              })
                                            }
                                          />
                                          {normalizeCurrencyValue(order.remainingValue) > 0 && (
                                            <Ticket
                                              className={'mr-4 svg-icon action-icon'}
                                              onClick={() =>
                                                onShouldShowModal({
                                                  value: ShouldShowModal.SALE,
                                                  newTitleModal: 'Criar novo pagamento',
                                                  order,
                                                })
                                              }
                                            />
                                          )}
                                        </Fragment>
                                      ),
                                    }))
                                  : []
                              }
                            />
                          </Fragment>
                        )}
                      </Fragment>
                    }
                  />
                </Fragment>
              ))}
            </Fragment>
          )}
        </Fragment>
      )}
      {/*
        <div key={order.orderId} className="efr-row">
          <div className="efr-row-header">
            <div className="efr-col efr-col-title">
              <strong>{itemLabel}</strong>
              <span className="efr-muted">{dayjs(order.orderDate).format('DD/MM/YYYY')}</span>
            </div>

            <div className="efr-col">
              <span>PDV</span>
              <strong>{order.pdv}</strong>
            </div>

            <div className="efr-col">
              <span>Total</span>
              <strong>{toCurrency(total)}</strong>
            </div>

            <div className="efr-col">
              <span>Pago</span>
              <strong>{toCurrency(paid)}</strong>
            </div>

            <div className="efr-col">
              <span>Falta</span>
              <strong>{toCurrency(remaining)}</strong>
            </div>
          </div>

          
          <div className="efr-progress">
            <div className="efr-progress-bar">
              <div className="efr-progress-fill" style={{ width: `${paidPct}%` }} />
            </div>
            <div className="efr-progress-label">{paidPct.toFixed(0)}% pago</div>
          </div>
        </div>
         */}
    </div>
  );
};
